import React, { useEffect, useRef } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Confirmation modal for actions that cannot be undone.
 *
 * Deliberately not dismissable by clicking the backdrop. Every caller uses this
 * for a destructive action, and a stray click landing outside the panel should
 * not be the thing that decides whether a record survives — Cancel, the close
 * button and Escape are all explicit.
 *
 * While `isLoading` is true the dialog refuses to close at all, including via
 * Escape: the request is already in flight and dismissing the dialog would hide
 * an outcome the user still needs to see.
 */
const ConfirmDialog = ({
    isOpen,
    title = "Are you sure?",
    message,
    details,
    confirmLabel = "Delete",
    cancelLabel = "Cancel",
    isLoading = false,
    onConfirm,
    onCancel,
}) => {
    const cancelRef = useRef(null);

    // Escape closes; focus starts on Cancel so a stray Enter cannot confirm.
    useEffect(() => {
        if (!isOpen) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape" && !isLoading) onCancel?.();
        };

        document.addEventListener("keydown", onKeyDown);
        cancelRef.current?.focus();

        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, isLoading, onCancel]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
        >
            <div
                className="w-full max-w-md rounded-2xl overflow-hidden"
                style={{
                    background: "var(--card-bg)",
                    border: "1px solid var(--card-border)",
                }}
            >
                {/* Header */}
                <div
                    className="flex items-start justify-between gap-4 px-6 py-4 border-b"
                    style={{ borderColor: "var(--card-border)" }}
                >
                    <div className="flex items-start gap-3">
                        <span
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                            style={{ background: "rgba(239, 68, 68, 0.12)" }}
                        >
                            <AlertTriangle size={20} style={{ color: "var(--danger)" }} />
                        </span>

                        <div>
                            <h3
                                id="confirm-dialog-title"
                                className="text-lg font-semibold"
                                style={{ color: "var(--main-text)" }}
                            >
                                {title}
                            </h3>
                            <p
                                className="text-sm mt-1"
                                style={{ color: "var(--main-text-muted)" }}
                            >
                                This action cannot be undone.
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={onCancel}
                        disabled={isLoading}
                        aria-label="Close"
                        className="p-2 rounded-lg hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ color: "var(--main-text-muted)" }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    <p className="text-sm" style={{ color: "var(--main-text)" }}>
                        {message}
                    </p>

                    {details && (
                        <div
                            className="mt-4 rounded-lg border px-4 py-3 text-sm"
                            style={{
                                background: "var(--main-bg)",
                                borderColor: "var(--card-border)",
                                color: "var(--main-text-muted)",
                            }}
                        >
                            {details}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex justify-end gap-3 px-6 py-4 border-t"
                    style={{ borderColor: "var(--card-border)" }}
                >
                    <button
                        ref={cancelRef}
                        onClick={onCancel}
                        disabled={isLoading}
                        className="h-10 px-4 rounded-lg text-sm font-medium border disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                            borderColor: "var(--card-border)",
                            color: "var(--main-text)",
                            background: "var(--card-bg)",
                        }}
                    >
                        {cancelLabel}
                    </button>

                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className="h-10 px-4 rounded-lg text-sm font-medium text-white flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{ background: "var(--danger)" }}
                    >
                        {isLoading && (
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        )}
                        {isLoading ? "Deleting..." : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
